import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { Loader2, Save, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

const sel = 'text-xs border border-slate-200 rounded-md px-2 h-8 bg-white w-full';

const Rules = ({ s, setS, onSave, admin }) => (
  <Card className="p-4 space-y-3" data-testid="hlt-rules-card">
    <h3 className="text-sm font-bold text-slate-900">Incentive rules</h3>
    <label className="block text-xs text-slate-600">Amount per qualifying Hot lead (₹)
      <Input type="number" min="0" value={s.rate} disabled={!admin} onChange={e => setS({ ...s, rate: e.target.value })} className="h-8 text-xs mt-1 w-32" data-testid="hlt-rate-input" /></label>
    <div className="flex items-center gap-2 text-xs text-slate-700">
      <Switch checked={s.pay_unlinked} disabled={!admin} onCheckedChange={v => setS({ ...s, pay_unlinked: v })} data-testid="hlt-pay-unlinked-switch" />
      {s.pay_unlinked ? 'Pay leads whose Emp ID is not linked yet (flagged)' : 'Hold leads until their Emp ID is linked'}
    </div>
    <label className="block text-xs text-slate-600">Count leads that turned Hot on or after
      <Input type="date" value={s.start_date} disabled={!admin} onChange={e => setS({ ...s, start_date: e.target.value })} className="h-8 text-xs mt-1 w-40" data-testid="hlt-start-date-input" /></label>
    <label className="block text-xs text-slate-600">Emp IDs left out of this report (comma separated)
      <Input value={s.excluded} disabled={!admin} onChange={e => setS({ ...s, excluded: e.target.value })} className="h-8 text-xs mt-1" data-testid="hlt-excluded-input" /></label>
    <ul className="text-[11px] text-slate-500 list-disc pl-4 space-y-0.5">
      <li>Lead Status is Hot Lead now, and was Suspect or Call Back just before it turned Hot</li>
      <li>Sales User Lead Status is not Unable to Reach · no lead-owner check</li>
      <li>Credited to the Inbound Sales Person Emp ID, in the month of the Hot conversion date</li>
    </ul>
    {admin && <Button size="sm" onClick={onSave} data-testid="hlt-save-rules-btn"><Save className="w-3.5 h-3.5 mr-1.5" />Save rules</Button>}
  </Card>
);

const MapRow = ({ m, opts, admin, onSave, onDelete }) => {
  const [r, setR] = useState(m);
  useEffect(() => setR(m), [m]);
  const id = (r.emp_id || 'new').replace(/[^a-zA-Z0-9]/g, '-');
  return (
    <tr className="border-t border-slate-100" data-testid={`hlt-map-row-${id}`}>
      <td className="py-1.5 pr-2">{m.emp_id ? <span className="font-mono">{m.emp_id}</span> : (
        <select className={sel} value={r.emp_id} onChange={e => setR({ ...r, emp_id: e.target.value })} data-testid="hlt-map-new-empid">
          <option value="">Pick Emp ID…</option>{opts.emp_ids.map(x => <option key={x} value={x}>{x}</option>)}</select>)}</td>
      <td className="pr-2"><Input value={r.name} disabled={!admin} onChange={e => setR({ ...r, name: e.target.value })} placeholder="Employee name" className="h-8 text-xs" data-testid={`hlt-map-name-${id}`} /></td>
      <td className="pr-2"><select className={sel} disabled={!admin} value={r.crm_user_id} data-testid={`hlt-map-user-${id}`}
        onChange={e => { const u = opts.users.find(x => x.id === e.target.value); setR({ ...r, crm_user_id: e.target.value, crm_user_name: u?.name || '' }); }}>
        <option value="">— no CRM user —</option>{opts.users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        {r.crm_user_id && !opts.users.some(u => u.id === r.crm_user_id) && <option value={r.crm_user_id}>{r.crm_user_name}</option>}</select></td>
      {admin && <td className="whitespace-nowrap text-right">
        <Button size="sm" variant="outline" className="h-8" onClick={() => onSave(r)} data-testid={`hlt-map-save-${id}`}><Save className="w-3.5 h-3.5" /></Button>
        {m.emp_id && <Button size="sm" variant="ghost" className="h-8 text-rose-600" onClick={() => onDelete(m.emp_id)} data-testid={`hlt-map-delete-${id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}</td>}
    </tr>
  );
};

export const HltSettings = ({ onChanged }) => {
  const { user } = useAuth();
  const admin = user?.role === 'admin';
  const [s, setS] = useState(null);
  const [maps, setMaps] = useState([]);
  const [opts, setOpts] = useState({ emp_ids: [], users: [] });
  const [adding, setAdding] = useState(false);
  const load = useCallback((keepRules) => api.get('/hot-transfer/settings').then(r => {
    if (!keepRules) setS(cur => cur || { ...r.data.settings, excluded: r.data.settings.excluded_emp_ids.join(', ') });
    setMaps(r.data.mappings);
  }), []);
  useEffect(() => {
    load();
    if (admin) api.get('/inside-sales-audit/crm-options').then(r => setOpts(r.data)).catch(() => toast.error('Could not read CRM users'));
  }, [load, admin]);

  const saveRules = async () => {
    try { await api.put('/hot-transfer/settings', { rate: s.rate, pay_unlinked: s.pay_unlinked, excluded_emp_ids: s.excluded.split(','), start_date: s.start_date }); toast.success('Rules saved'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const saveMap = async r => {
    try { await api.put('/hot-transfer/mappings', r); toast.success(`${r.emp_id} linked`); setAdding(false); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const delMap = async id => {
    try { await api.delete(`/hot-transfer/mappings/${encodeURIComponent(id)}`); toast.success('Link removed'); load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  if (!s) return <div className="py-16 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const unmapped = opts.emp_ids.filter(x => x !== 'Direct' && !s.excluded_emp_ids.includes(x) && !maps.some(m => m.emp_id === x));
  return (
    <div className="grid lg:grid-cols-3 gap-4 items-start" data-testid="hlt-settings-tab">
      <Rules s={s} setS={setS} onSave={saveRules} admin={admin} />
      <Card className="p-4 lg:col-span-2" data-testid="hlt-mapping-card">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-bold text-slate-900">Emp ID → employee / CRM user</h3>
          {admin && !adding && <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="hlt-map-add-btn"><Plus className="w-3.5 h-3.5 mr-1" />Link an Emp ID</Button>}
        </div>
        <p className="text-[11px] text-slate-500 mb-3">{admin ? `${unmapped.length} Emp ID(s) in the CRM picklist are not linked yet${unmapped.length ? `: ${unmapped.join(', ')}` : ''}.` : 'Only an admin can change these links.'}</p>
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr><th className="text-left py-1 w-40">Emosys Emp ID</th><th className="text-left">Employee name</th><th className="text-left">Zoho CRM user</th>{admin && <th />}</tr></thead>
          <tbody>
            {maps.map(m => <MapRow key={m.emp_id} m={m} opts={opts} admin={admin} onSave={saveMap} onDelete={delMap} />)}
            {adding && <MapRow m={{ emp_id: '', name: '', crm_user_id: '', crm_user_name: '' }} opts={{ ...opts, emp_ids: unmapped }} admin={admin} onSave={saveMap} onDelete={() => {}} />}
            {!maps.length && !adding && <tr><td colSpan={4} className="text-center text-slate-400 py-8">No Emp IDs linked yet.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
};
